import React from 'react';
import { Dialog, Grid, Typography, Box, Divider } from '@mui/material';

import { useTheme } from '@emotion/react';
import { Pendingwarning } from 'component/BuyersMarketplace/MakeOffers';
import { poppins400, poppins500, poppins600 } from 'theme/common.style';
import { Button, useMediaQuery } from '@mui/material';

const PendingPrivatePopup = ({
  open,
  onClose,
  onContinue,
  pendingUserId,
  address,
  manageOfferData,
  onContinues,
  onCloses,
  tableData,
  pendingOfferType,
  fromTableHead = false,
  currentPrivateOfferPrice,
  vendorAcceptedAmount,
}) => {
  const theme = useTheme();
  const matchessm = useMediaQuery(theme.breakpoints.down('sm'));
  const matchesmd = useMediaQuery(theme.breakpoints.down('md'));

  const getCounterOfferAmount = () => {
    if (!pendingUserId || !tableData?.BuyerRegisterDetails) return null;

    const buyer = tableData.BuyerRegisterDetails.find(
      (b) => b?.buyerDetails?.user?.userId === pendingUserId
    );

    if (buyer?.prevForwardedOfferPrice) {
      return buyer.prevForwardedOfferPrice;
    }

    return null;
  };

  const formatCurrency = (amount) => {
    if (amount == null || amount == undefined || amount == '') return '';

    let numericAmount = amount;
    if (typeof amount == 'string') {
      const cleaned = amount.replace(/[^0-9.-]+/g, '');
      numericAmount = parseFloat(cleaned);
      if (isNaN(numericAmount)) return '';
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numericAmount);
  };

  const getComparisonType = () => {
    if (fromTableHead || !pendingUserId) return null;

    const counterAmount = Number(
      String(getCounterOfferAmount()).replace(/[$,]/g, '')
    );
    if (!counterAmount || !currentPrivateOfferPrice) return null;

    if (currentPrivateOfferPrice < counterAmount) {
      return 'lower';
    } else if (currentPrivateOfferPrice > counterAmount) {
      return 'higher';
    } else {
      return 'same';
    }
  };

  const getTitleText = () => {
    if (fromTableHead) {
      return 'Pending Offers Detected';
    }

    const comparisonType = getComparisonType();

    if (comparisonType === 'lower') {
      return 'Higher Counter Offer Detected';
    } else if (comparisonType === 'higher') {
      return 'Lower Counter Offer Detected';
    } else if (comparisonType === 'same') {
      return 'Same Counter Offer Detected';
    }

    return 'Pending Counter Offer Detected';
  };

  const getDescriptionText = () => {
    if (fromTableHead) {
      const formattedCurrentAmount = formatCurrency(currentPrivateOfferPrice);

      return (
        <Box>
          <Typography
            sx={{
              ...poppins400,
              fontSize: matchesmd ? '14px' : '16px',
              color: '#666666',
              mb: 2,
              textAlign: 'justify',
            }}
          >
            Some buyers have pending offers. Sending a new private offer of{' '}
            <strong>{formattedCurrentAmount}</strong> will automatically
            withdraw the existing offer to ensure only one active offer remains
            for each buyer.
          </Typography>
        </Box>
      );
    }

    const comparisonType = getComparisonType();
    const counterAmount = getCounterOfferAmount();
    const formattedCounterAmount = formatCurrency(counterAmount);
    const formattedCurrentAmount = formatCurrency(currentPrivateOfferPrice);

    const buyerText = 'this buyer';

    if (comparisonType == 'lower') {
      return (
        <Box>
          <Typography
            sx={{
              ...poppins400,
              fontSize: matchesmd ? '14px' : '16px',
              color: '#666666',
              mb: 2,
              textAlign: 'justify',
            }}
          >
            You have already shared a counter offer of{' '}
            <strong>{formattedCounterAmount}</strong> with {buyerText}. You are
            now forwarding a lower private offer of{' '}
            <strong>{formattedCurrentAmount}</strong>. <br />
            <br />
            This may create confusion for buyers and override the current offer
            position.
          </Typography>
        </Box>
      );
    } else if (comparisonType == 'higher') {
      return (
        <Box>
          <Typography
            sx={{
              ...poppins400,
              fontSize: matchesmd ? '14px' : '16px',
              color: '#666666',
              mb: 2,
              textAlign: 'justify',
            }}
          >
            You have already shared a counter offer of{' '}
            <strong>{formattedCounterAmount}</strong> with {buyerText}. You are
            now forwarding a higher private offer of{' '}
            <strong>{formattedCurrentAmount}</strong>. <br />
            <br />
            This may create confusion for buyers and override the current offer
            position.
          </Typography>
        </Box>
      );
    } else if (comparisonType == 'same') {
      return (
        <Box>
          <Typography
            sx={{
              ...poppins400,
              fontSize: matchesmd ? '14px' : '16px',
              color: '#666666',
              mb: 2,
              textAlign: 'justify',
            }}
          >
            You have already shared a counter offer of{' '}
            <strong>{formattedCounterAmount}</strong> with {buyerText}. You are
            now forwarding a private offer with the same amount of{' '}
            <strong>{formattedCurrentAmount}</strong>. <br />
            <br />
            This may override the current offer position.
          </Typography>
        </Box>
      );
    }

    return '';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          minWidth: matchessm ? '95%' : '550px',
          maxWidth: '600px',
          boxShadow: '0px 8px 30px rgba(0, 0, 0, 0.12)',
          padding: 0,
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          p: matchessm ? 3 : 4,
        }}
      >
        <Grid container spacing={0}>
          <Grid item xs={12} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Pendingwarning />
              <Typography
                sx={{
                  ...poppins500,
                  fontSize: matchessm ? '18px' : '22px',
                  textAlign: 'left',
                }}
              >
                {getTitleText()}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sx={{ mb: 4 }}>
            {getDescriptionText()}
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
              }}
            >
              <Button
                variant="outlined"
                sx={{
                  ...poppins500,
                  padding: matchessm ? '8px 28px' : '10px 36px',
                  height: matchessm ? '40px' : '44px',
                  fontSize: matchessm ? '13px' : '14px',
                  color: '#666666',
                  border: '1px solid #E0E0E0',
                  borderRadius: '8px',
                  textTransform: 'none',
                  '&:hover': {
                    border: '1px solid #999999',
                    backgroundColor: 'transparent',
                  },
                }}
                onClick={onClose || onCloses}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={{
                  ...poppins500,
                  padding: matchessm ? '8px 28px' : '10px 36px',
                  height: matchessm ? '40px' : '44px',
                  fontSize: matchessm ? '13px' : '14px',
                  color: '#FFFFFF',
                  backgroundColor: '#4CA4D6',
                  borderRadius: '8px',
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#3A8AB8',
                    boxShadow: 'none',
                  },
                }}
                onClick={onContinue || onClose}
              >
                Forward Offer
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
};

export default PendingPrivatePopup;
